import React from 'react';
import { GenericFormPage } from '../../components/generic';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { useProject, Project } from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';

const Form: React.FC = () => {
  const configWithOptions = (() => {
    const fields = moduleConfig.fields.map((field: FieldConfig) => {
      if (field.name === 'clientId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Client',
          placeholder: 'Select a client',
          options: [],
          apiEndpoint: '/crm-clients',
          labelField: 'clientName',
          valueField: 'clientId' as const,
        };
      }
      if (field.name === 'projectStatusId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Status',
          placeholder: 'Select status',
          options: [],
          apiEndpoint: '/p-project-status',
          labelField: 'ps_descr',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'defaultAgreementId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Default Support Agreement',
          placeholder: 'Select default agreement (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/crm-support-agreements',
          labelField: 'description',
          valueField: 'id' as const,
        };
      }
      return field;
    });

    // Form data allows foreign keys as string for select components
    type FormProject = Omit<Project, 'clientId' | 'projectStatusId' | 'defaultAgreementId'> & {
      clientId?: string | number;
      projectStatusId?: string | number;
      defaultAgreementId?: string | number;
    };

    // Custom mapItemToFormData that converts foreign keys to string for select
    const customMapItemToFormData = (item: Project): Partial<FormProject> => {
      const baseData = mapItemToFormData(item);
      return {
        ...baseData,
        clientId: baseData.clientId ? String(baseData.clientId) : '',
        projectStatusId: baseData.projectStatusId ? String(baseData.projectStatusId) : '',
        defaultAgreementId: baseData.defaultAgreementId ? String(baseData.defaultAgreementId) : '',
      };
    };

    // Custom getInitialFormData
    const customGetInitialFormData = (): Partial<FormProject> => {
      return {
        ...getInitialFormData(),
        clientId: '',
        projectStatusId: '',
        defaultAgreementId: '',
      };
    };

    // Helper function to convert string to number or undefined
    const convertToNumber = (value: string | number | undefined): number | undefined => {
      if (value === undefined || value === null || String(value) === '') {
        return undefined;
      }
      return Number(value);
    };

    // Helper function to convert date-only strings to ISO-8601 datetime strings
    const convertDateToDateTime = (value: string | undefined): string | undefined => {
      if (value === undefined || value === null || String(value).trim() === '') {
        return undefined;
      }
      const dateStr = String(value).trim();
      
      if (dateStr.includes('T') || dateStr.includes('Z')) {
        return dateStr;
      }
      
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const date = new Date(dateStr + 'T00:00:00.000Z');
        return date.toISOString();
      }
      
      try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString();
        }
      } catch {
        // If parsing fails, return the original value
      }
      
      return dateStr;
    };

    // Custom create/update handlers that convert foreign key strings back to numbers
    const customCreate = async (data: Partial<FormProject>) => {
      const processedData: Partial<Project> = {
        ...data,
        clientId: convertToNumber(data.clientId) as number, // Required field
        projectStatusId: convertToNumber(data.projectStatusId) as number, // Required field
        defaultAgreementId: convertToNumber(data.defaultAgreementId),
        projectStartDate: convertDateToDateTime(data.projectStartDate) as string, // Required field
        projectEndDate: convertDateToDateTime(data.projectEndDate),
      };
      return await moduleConfig.form.create(processedData);
    };

    const customUpdate = async (id: number, data: Partial<FormProject>) => {
      const processedData: Partial<Project> = {
        ...data,
        clientId: convertToNumber(data.clientId) as number, // Required field
        projectStatusId: convertToNumber(data.projectStatusId) as number, // Required field
        defaultAgreementId: convertToNumber(data.defaultAgreementId),
        projectStartDate: convertDateToDateTime(data.projectStartDate) as string, // Required field
        projectEndDate: convertDateToDateTime(data.projectEndDate),
      };
      return await moduleConfig.form.update(id, processedData);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData: customGetInitialFormData as () => Partial<Project>,
      mapItemToFormData: customMapItemToFormData as (item: Project) => Partial<Project>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<Project>) => Promise<Project>,
        update: customUpdate as (id: number, data: Partial<Project>) => Promise<Project>,
      },
    };
  })();

  return (
    <GenericFormPage<Project>
      useData={useProject}
      config={configWithOptions}
    />
  );
};

export default Form;

